import { makeMonitorRepository } from "../repositories/repositoryBuilders.js";
import Cache from "../../models/Cache.js";
import componentNodeFactory from "../../models/keyFigure/componentNodeFactory.js";
import KeyFigureService from "./KeyFigureService.js";
import optionsBag from "../../models/options/optionsBag.js";
import session from "../session.js";

/**
 * Dictionary mapping a company id to an instance of KeyFigureService.
 *
 * @type {{[key:string]: KeyFigureService}}
 */
let services = {};

/**
 * Flush the services cache.
 */
export function flushServices() {
	services = {};
}

function createKeyFigureRepository(spec = {}) {
	spec.model ||= componentNodeFactory;

	optionsBag.fromId("lrfk-kpi").whenActive(() => {
		spec.defaultFilters = {
			...spec.defaultFilters,
			includeLrfkFarmerKeyFigures: true,
		};
	});

	spec.antiForgeryToken = session.getAntiForgeryToken();

	return makeMonitorRepository(spec);
}

/**
 * Factory method to retrieve or create a KeyFigureService for a
 * specific ownerId (e.g., an agency or company id).
 *
 * If no ownerId is provided, fallbacks to the current session's
 * company id. If there is a company in the session (in Accounting),
 * most of the function's parameters are optional.
 *
 * KeyFigureService instances are cached for performance.
 *
 * @param {object} [spec] - Object parameters
 * @param {string} [spec.ownerId] - Identifier of the entity (e.g.,
 * company or agency) owning the key figures.
 * @param {Cache} [spec.cache] - Cache for key figures. It defaults to `new Cache()`
 * @param {object} [spec.repositorySpec] - Spec of the repository used to fetch key figures.
 * The segment used to refer to the keyFigure id must be named `id`
 * @param {function} [spec.serviceBuilder] - Only useful for unit
 * tests. A function to use to create new instances of the service.
 * @param {function} [spec.repositoryBuilder] - Only useful for unit
 * tests. A function to use to create new instances of the repository.
 * @return {KeyFigureService}
 */
export default function getKeyFigureService({
	ownerId,
	cache = new Cache(),
	repositorySpec,
	serviceBuilder = (...rest) => new KeyFigureService(...rest),
	repositoryBuilder = createKeyFigureRepository,
} = {}) {
	const isAccounting = !ownerId;
	ownerId ||= session.getCompanyId();

	if (services[ownerId]) {
		return services[ownerId];
	}

	repositorySpec ||= isAccounting
		? {
				route: "/Accounting2/Company/#ownerId/KeyFigure/?id",
				defaultFilters: { ownerId },
			}
		: undefined;

	const newService = serviceBuilder({
		cache,
		repository: repositoryBuilder(repositorySpec),
	});

	services[ownerId] = newService;
	return newService;
}
